/* oxlint-disable react-hooks/rules-of-hooks -- tracked in docs/oxlint-violations.md */
import { useTranslation } from 'react-i18next';

import { Body2, RichText } from '@myphx-engineering/phx-design-system';

import type {
    MailOrderProvider,
    PharmacyProvider,
} from '../../../../graphql/generated/graphql';
import { useMedicationDisclaimers } from '../../../../hooks/use-medication-disclosures';
import { usePharmacyName } from '../../../../hooks/use-pharmacy-name';
import type { PrescriptionOffer } from '../../../../hooks/use-prescription-offers';
import { isPharmacySpecificOffer } from '../../../../util/offers/is-pharmacy-specific-offer';
import type { OrderPricingProps } from '../../order-confirmation/shared/OrderPricing';

import type { PricingCardElementProps } from './shared';

export type PricingDisclaimerProps = {
    offer?: OrderPricingProps['selectedOffer'] | PrescriptionOffer | undefined;
    medication: PricingCardElementProps['medication'];
    priceType: PricingCardElementProps['priceType'];
    showDisclaimer: boolean;
    termsDisclaimer?: string;
};

export const PricingDisclaimer = ({
    offer,
    medication,
    showDisclaimer,
    priceType,
    termsDisclaimer = '',
}: PricingDisclaimerProps) => {
    const { t } = useTranslation('lilly');

    const richDisclaimers = useMedicationDisclaimers({
        headlinePriceType: priceType,
        medication,
        slot: 'price-card',
    });

    const provider =
        offer && 'provider' in offer ? offer.provider : offer?.pharmacyProvider;

    const pharmacyName =
        provider?.__typename === 'UnknownProvider'
            ? undefined
            : usePharmacyName(provider as MailOrderProvider | PharmacyProvider);
    const isPharmacySpecificPrice = isPharmacySpecificOffer(offer);

    const showRichDisclaimer = !!richDisclaimers.length;

    const disclaimer =
        pharmacyName && isPharmacySpecificPrice
            ? t('fill.pricing.feesMayApplyWithPharmacy', {
                  pharmacyName,
                  termsDisclaimer,
              })
            : t('fill.pricing.feesMayApply', { termsDisclaimer });

    if (!showDisclaimer) {
        return null;
    }

    if (showRichDisclaimer) {
        return richDisclaimers.map((richDisclaimer) => {
            const richContent = richDisclaimer.content;

            return richContent.map(({ key, ...richLine }) => (
                <RichText key={key} {...richLine} values={{ pharmacyName }} />
            ));
        });
    }

    return <Body2>{disclaimer}</Body2>;
};
